import { type ReactElement, useEffect } from 'react';
import Head from 'next/head';
import getConfig from 'next/config';
import { connect } from 'react-redux';
import type { MapPOI } from 'shared/map/interfaces';
import { PoiTypes } from 'shared/map/pois/poiTypes';

import { useInterval } from '@/hooks/useInterval';
import { enrichOfferWithTrackingData, getDirectOffers, mapKayakOffer } from '@/utils/integration/hotelOfferMapper';
import { extractNameFromKey } from '@/utils/kayakKeys';
import { trackHotelView } from '@/utils/tracking/finnAnalytics';
import { hasDirectMapping } from '@/utils/integration/providers';
import { setInitialBannerFilterValues } from 'shared/banners/bannerParams';
import { fetchDirectPrices } from '@/state/directPricesActions';
import { pollHotel } from '@/state/hotelPageActions';

import {
    type AccommodationState,
    ApiActionStatus,
    type DirectHotelPrices,
    type Filters,
    type HotelImage,
    type HotelOffer,
    type HotelPageState,
    KayakHotelProvider,
} from '@/interfaces';
import { Broadcast } from 'shared/page/Broadcast';
import { HotelBreadcrumbs } from '@/components/hotelBreadcrumbs/HotelBreadcrumbs';
import { HotelImages } from './components/hotelImages/HoteImages';
import { HotelMap } from '@/components/hotelMap/HotelMap';
import { HotelOffers, sortOffers } from './containers/hotelOffers/HotelOffers';
import { HotelData } from './components/hotelData/HotelData';
import { updateHotelPageQueryParams } from '@/utils/url/routerHelpers';
import { recordSessionTrafficSource } from '@/utils/tracking/sourceCookie';
import type { ExtraHotelData } from '@/server/integration/extraHotelDataResponseTypes';
import { getTitle } from '@/utils/title';

const { publicRuntimeConfig } = getConfig();

// TODO REIS-5230: Identical to POI_TYPES in SearchResultsMap.tsx. Extract to common list.
const POI_TYPES: readonly PoiTypes[] = [
    PoiTypes.AVINOR_AIRPORT,
    PoiTypes.ENTUR_AIRPORT,
    PoiTypes.ENTUR_BUS,
    PoiTypes.ENTUR_FERRY,
    PoiTypes.ENTUR_SUBWAY,
    PoiTypes.ENTUR_TRAIN,
    PoiTypes.ENTUR_TRAM,
    PoiTypes.CHARGING_STATION,
    PoiTypes.STORE,
    PoiTypes.SHOPPING_CENTRE,
    PoiTypes.PHARMACY,
    PoiTypes.KIOSK,
    PoiTypes.GAS_STATION,
    PoiTypes.SPORTS_FACILITY,
    PoiTypes.GYM,
    PoiTypes.SKI_CENTRE,
    PoiTypes.TRAVEL,
    PoiTypes.TOURIST_ATTRACTION,
    PoiTypes.MUSEUM,
    PoiTypes.HOSPITAL,
];

interface Props {
    hotelPage: HotelPageState;
    directPrices?: DirectHotelPrices;
    filters: Filters;
    extraHotelData: ExtraHotelData;
}

interface DispatchProps {
    startFetchDirectPrices: (hotelId: number) => void;
    triggerPoll: () => void;
}

export const HotelPage = ({
    hotelPage,
    directPrices,
    filters,
    startFetchDirectPrices,
    triggerPoll,
    extraHotelData,
}: Props & DispatchProps): ReactElement<Props & DispatchProps> => {
    const hotelId = hotelPage.id;

    const countryKey = extractNameFromKey(hotelPage.place?.hierarchy[0]?.key);
    let placeKey = extractNameFromKey(hotelPage.wellKnownPlace?.key);
    if (placeKey === countryKey) {
        placeKey = extractNameFromKey(hotelPage.place?.key);
    }
    const hotelKey = extractNameFromKey(hotelPage.key);

    const hotelHasDirectMapping = hasDirectMapping(hotelId);

    useEffect(() => {
        if (hotelHasDirectMapping && filters.checkInDate && filters.checkOutDate && filters.rooms) {
            startFetchDirectPrices(hotelId);
        }
    }, [filters.checkInDate, filters.checkOutDate, filters.rooms]);

    useEffect(() => {
        trackHotelView(hotelId, hotelPage.place.hierarchy);
        setInitialBannerFilterValues({
            destination: {
                city: hotelPage.wellKnownPlace?.name,
                country: hotelPage.place?.hierarchy?.[0]?.name,
            },
        });
        recordSessionTrafficSource(window.location.search);
        updateHotelPageQueryParams(filters);
    }, []);

    useInterval(
        (): void => {
            if (!hotelPage.isComplete) {
                triggerPoll();
            }
        },
        !hotelPage.isComplete ? 2000 : null,
    );

    if (!hotelId) {
        // Abort early
        return <div>No hotel found</div>;
    }

    const numberOfRoomsSearched = filters.rooms ? filters.rooms.length : 1;

    // Kayak
    const kayakOffers: HotelOffer[] = hotelPage.results
        .map((offer) => mapKayakOffer(offer, hotelPage.providers[offer.providerIndex], numberOfRoomsSearched))
        .map((offer) =>
            enrichOfferWithTrackingData(offer, countryKey, placeKey, filters.checkInDate, filters.checkOutDate, hotelKey, hotelId),
        );

    const revatoOffers = kayakOffers.filter((offer) => offer.provider.code === KayakHotelProvider.REVATO);
    const directOffersFromKayak = kayakOffers.filter(
        (offer) => offer.provider.isDirect && offer.provider.code !== KayakHotelProvider.REVATO,
    );
    const otaOffers = kayakOffers.filter((offer) => !offer.provider.isDirect && offer.provider.code !== KayakHotelProvider.REVATO);

    const directOffers: HotelOffer[] = (
        hotelHasDirectMapping
            ? getDirectOffers(directPrices, hotelId, numberOfRoomsSearched).map((offer) =>
                  enrichOfferWithTrackingData(offer, countryKey, placeKey, filters.checkInDate, filters.checkOutDate, hotelKey, hotelId),
              )
            : directOffersFromKayak.length > 0
              ? directOffersFromKayak
              : revatoOffers
    ).sort((a, b) => a.totalRate + a.handicapRoomWeighting - (b.totalRate + b.handicapRoomWeighting));

    const providerOffers = otaOffers.sort((a, b) => a.totalRate - b.totalRate);
    const allOffers = sortOffers(directOffers, otaOffers);

    const canonical = `https://finn.no${publicRuntimeConfig.basePath}/detaljer/${hotelKey}/`.toLowerCase();
    const ogImage = hotelPage.images.length > 0 ? hotelPage.images[0].large : 'https://www.finn.no/assets/finn-logo-large.png';
    const hotelName = extraHotelData?.name ?? hotelPage.name;
    const description = extraHotelData?.description ?? hotelPage.description;
    const selectedPoi: MapPOI = {
        name: hotelName,
        coordinates: [hotelPage.longitude, hotelPage.latitude],
    };

    const extraImages: HotelImage[] = extraHotelData?.images?.map((image) => ({ large: image.url, caption: image.description }));
    return (
        <>
            <Head>
                <title>{getTitle(hotelName)}</title>
                <meta name="description" content={description} />
                <meta property="og:description" content={description} />
                <meta property="og:title" content={hotelName} />
                <meta property="og:image" content={ogImage} />
                <meta property="og:url" content={canonical} />
                <meta name="robots" content="noindex, nofollow" />
                <link rel="canonical" href={canonical} />
            </Head>

            <Broadcast url="https://www.finn.no/broadcasts/?path=www.finn.no/reise/hotell" troika />
            <div data-testid="HotelPage" className="u-mh16 u-mt16--gt768">
                <HotelBreadcrumbs
                    place={hotelPage.wellKnownPlace ?? hotelPage.place}
                    hotel={hotelPage}
                    extraHotelData={extraHotelData}
                    filters={filters}
                />
                <HotelImages id={hotelId} name={hotelName} images={extraImages || hotelPage.images} />
                <HotelData
                    hotelKey={hotelKey}
                    hotelName={hotelName}
                    description={extraHotelData?.description ?? hotelPage.description}
                    starRating={hotelPage.starRating}
                    address={hotelPage.address}
                    placeName={hotelPage.wellKnownPlace?.name}
                    bestOffer={allOffers[0]}
                    reviews={hotelPage.reviews}
                    features={extraHotelData?.amenities ?? hotelPage.features}
                    filters={filters}
                    latitude={hotelPage.latitude}
                    longitude={hotelPage.longitude}
                />
                <HotelOffers
                    hotelId={hotelPage.id}
                    countryKey={countryKey}
                    hotelKey={hotelKey}
                    directOffers={directOffers}
                    providerOffers={providerOffers}
                    directIsPending={directPrices.status === ApiActionStatus.PENDING}
                    providersIsPending={hotelPage.status === ApiActionStatus.PENDING || !hotelPage.isComplete}
                />
                <HotelMap
                    id="hotel-page-map"
                    selectedPois={[selectedPoi]}
                    poiTypes={POI_TYPES}
                    className="panel hotel-page-map-container"
                    disableScrollZoomUnlessFocused
                    fullscreenControl
                />
            </div>
        </>
    );
};

const mapStateToProps = (state: AccommodationState): Omit<Props, 'extraHotelData'> => ({
    hotelPage: state.hotelPage,
    directPrices: state.directPrices,
    filters: state.filters,
});

const mapDispatchToProps = {
    startFetchDirectPrices: fetchDirectPrices,
    triggerPoll: pollHotel,
};

export default connect(mapStateToProps, mapDispatchToProps)(HotelPage);
